import React, { useState } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Shown when an answer was produced with retrieval in reduced mode (#20). A
 * bare "search degraded" label is meaningless to a user, so this is a clickable
 * chip (with a native tooltip) that opens a modal explaining what happened and
 * what to do about it.
 */
const DegradedSearchNotice: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Why am I seeing this?"
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-warn-bg text-warn hover:bg-warn-bg transition-colors"
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        Reduced results — why?
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Search ran in reduced mode"
        >
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setOpen(false)}
          />
          <div className="relative bg-surface rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-warn flex-shrink-0" />
                <h3 className="text-base font-semibold text-text-base">
                  Search ran in reduced mode
                </h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-text-faint hover:text-text-muted"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-sm text-text-muted">
              <p>
                Part of our search temporarily didn&apos;t respond, so this
                answer was built from a reduced set of sources.
              </p>
              <p>
                We normally combine <strong>meaning-based</strong> search with{" "}
                <strong>keyword</strong> search. One of those was briefly
                unavailable, so results came from the other alone — the answer
                may be less complete or miss relevant documents.
              </p>
              <p>
                This is usually temporary. If the answer looks thin,{" "}
                <strong>ask again in a moment</strong> to run the full search.
              </p>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-strong transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DegradedSearchNotice;
